import { useState, useEffect } from 'react';

export default function SchedulesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('');
  const [runResult, setRunResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (period) params.set('period', period);
      const r = await fetch(`/revrec/schedules?${params}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load schedules');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function runRecognition() {
    if (!period) return;
    setBusy(true);
    setError(null);
    setRunResult(null);
    try {
      const r = await fetch(`/revrec/run/${period}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Recognition run failed');
      setRunResult(d.data || d);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function fmtValue(cents, currency) {
    if (cents == null) return '—';
    return `${currency || ''} ${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`.trim();
  }

  function pctRecognized(scheduledCents, recognizedCents) {
    if (!scheduledCents || scheduledCents === 0) return '—';
    const pct = ((recognizedCents || 0) / scheduledCents) * 100;
    return `${pct.toFixed(1)}%`;
  }

  return (
    <section data-testid="revrec-schedules-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Recognition Schedules</h1>
          <p className="muted">View scheduled recognition amounts by period and run the recognition journal entries.</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="revrec-schedules-error">{error}</div>}

      {runResult && (
        <div className="alert success" data-testid="revrec-run-result">
          Recognition run complete for {runResult.period}: {runResult.entries_created ?? 0} entries created,{' '}
          {fmtValue(runResult.total_recognized_cents, runResult.currency)} recognized.
        </div>
      )}

      <div className="card form-card row-form">
        <label>Period (YYYY-MM)
          <input
            data-testid="revrec-period-input"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder="e.g. 2026-06"
          />
        </label>
        <div className="actions">
          <button className="btn ghost" onClick={load} disabled={loading}>Filter</button>
          <button
            className="btn primary"
            data-testid={period ? `revrec-run-${period}` : 'revrec-run-btn'}
            disabled={!period || busy}
            onClick={runRecognition}
          >
            Run recognition
          </button>
        </div>
      </div>

      <div className="card">
        <table className="data-table" data-testid="revrec-schedule-list">
          <thead>
            <tr>
              <th>Period</th>
              <th>Obligation</th>
              <th>Contract</th>
              <th>Scheduled</th>
              <th>Recognized</th>
              <th>% complete</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`revrec-schedule-row-${s.id}`}>
                <td>{s.period}</td>
                <td>{s.obligation_name || s.obligation_id}</td>
                <td>{s.contract_id}</td>
                <td>{fmtValue(s.scheduled_cents, s.currency)}</td>
                <td>{fmtValue(s.recognized_cents, s.currency)}</td>
                <td>{pctRecognized(s.scheduled_cents, s.recognized_cents)}</td>
                <td>
                  <span className={`badge ${s.status === 'recognized' ? 'success' : s.status === 'partial' ? 'warning' : ''}`}>
                    {s.status || 'pending'}
                  </span>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="muted">No schedules{period ? ` for period ${period}` : ''}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
